// All copy for the Typesetter page lives here. Edit text freely; the markup in
// TypesetterPage.js and the vignettes read from this file. Keep `appName` and
// `promise` on one line each: scripts/typesetter-images.py reads them to draw
// the Open Graph image.

export const APP_STORE_ID = '6736673526';
export const APP_STORE_URL =
  'https://apps.apple.com/ca/app/typesetter-book-notes/id6736673526';
export const SITE_URL = 'https://taci.uk';
export const PAGE_PATH = '/typesetter';
export const PRIVACY_PATH = '/typesetter-privacy';
export const SUPPORT_EMAIL = 'support@taci.uk';
export const COFFEE_URL = 'https://buymeacoffee.com/michaels.apps';

export const content = {
  appName: 'Typesetter',
  fullName: 'Typesetter: Book Notes',
  promise: 'Your reading life, kept in one quiet place.',
  copyright: 'Michael Taciuk',

  meta: {
    title: 'Typesetter: Book Notes for iPhone and iPad',
    description:
      'A reading companion for people who read on paper. Keep your shelves, time each session, take notes per book, and see goals, streaks and stats. No account, no tracking, backed up as plain text in your own iCloud Drive.',
    privacyTitle: 'Privacy Policy for Typesetter',
    privacyDescription:
      'How Typesetter handles your data: everything stays on your device and in your own iCloud Drive, there is no account and no analytics, and the only network requests are book lookups to Open Library.',
  },

  nav: {
    links: [
      { label: 'Shelves', href: '#shelves' },
      { label: 'Timer', href: '#timer' },
      { label: 'Notes', href: '#notes' },
      { label: 'Stats', href: '#stats' },
      { label: 'Your data', href: '#data' },
    ],
    compactCta: 'Get the app',
  },

  // Shown on desktop when an App Store link is clicked.
  qr: {
    label: 'iPhone and iPad app',
    heading: 'Scan to download',
    body: 'Point your iPhone camera at the code to open Typesetter on the App Store.',
    link: 'Or open the App Store page',
    alt: 'QR code linking to Typesetter on the App Store.',
  },

  hero: {
    supporting: 'A bookshelf, a timer, and a notebook for every book you read.',
    body: 'For people who read on paper and want to remember what they read. No account, no feed, no social layer. Everything stays on your phone and in your own iCloud Drive.',
    facts: ['No account', 'Works offline', 'iPhone and iPad'],
    imageAlt:
      'The Reading tab: a weekly goal bar at 3 hours 20 minutes of 5 hours, a 12-day streak, and a card for Emma by Jane Austen at page 286 of 474.',
  },

  shelves: {
    label: 'Your library',
    heading: 'Three shelves, one tap between them.',
    body: 'Bookshelf for what’s next, Currently Reading for what’s open, Finished Books for what’s done. Move a book along with one tap. Sort by date, title or recent activity, and search across titles, authors and the text of your notes.',
    cards: [
      {
        name: 'Bookshelf',
        caption: 'What’s next',
        body: 'The pile by the bed, without the pile.',
      },
      {
        name: 'Currently Reading',
        caption: 'What’s open',
        body: 'Log the page you’re on and the card shows how far through you are, with the timer a tap away.',
      },
      {
        name: 'Finished Books',
        caption: 'What’s done',
        body: 'Every finished book keeps its notes, its dates and its total time. This is the part you come back to.',
      },
    ],
    add: {
      heading: 'Add a book in seconds',
      body: 'Scan the barcode and the title, author, year and page count fill in from Open Library. Or type it in by hand.',
      alt: 'A scanned barcode next to a filled-in book form for Emma by Jane Austen, 1815, 474 pages.',
    },
  },

  timer: {
    label: 'The timer',
    heading: 'Put the phone down and read.',
    paragraphs: [
      'Start a session from the book’s notes page. The timer runs as a Live Activity in the Dynamic Island and on the lock screen, counting up while you read, so the phone can sit face down until you’re done.',
      'If the app is closed mid-session, it offers to recover the time on next launch. A dead battery or a wrong tap doesn’t lose the session.',
    ],
    lockAlt:
      'An iPhone lock screen at 9:41 with a Live Activity for Emma showing a reading timer counting up.',
    notesAlt:
      'The top of a book’s notes page with a Start reading button and the session timer.',
  },

  notes: {
    label: 'Notes',
    heading: 'A notebook for every book.',
    paragraphs: [
      'A rich text editor per book with headings, bold, italic and lists. Write as you go, or sit down after a chapter.',
      'Point the camera at a passage and it’s recognised and dropped into your notes. Share a book’s notes, or export the whole library.',
    ],
    alt: 'The notes editor for Emma with a chapter heading, a short paragraph, a bulleted list, and a passage captured from the camera.',
  },

  stats: {
    label: 'Goals, streaks and stats',
    heading: 'Numbers worth looking at.',
    body: 'Set a weekly reading goal, watch the bar fill, and keep a daily streak going. A gentle celebration when the goal is crossed, and nothing more.',
    points: [
      {
        title: 'A dedicated Stats tab',
        body: 'This week, streaks and all-time totals. A twelve-week chart against your goal, reading by weekday, and time per book.',
      },
      {
        title: 'A year in review',
        body: 'Books per month and your top genres, once the year has some reading in it.',
      },
      {
        title: 'Reminders that know when to stay quiet',
        body: 'A daily nudge at a time you choose, skipped on days you’ve already read. A streak saver in the evening when a streak is about to end. A Sunday check-in with the week’s numbers against the goal.',
      },
    ],
    statsAlt:
      'The Stats tab: 3 hours 20 minutes this week over 4 days, a 12-day streak, and a twelve-week bar chart against a 5-hour goal line.',
    widget: {
      heading: 'On the home screen',
      body: 'Weekly goal ring, streak and the book you’re reading, in small and medium sizes plus lock screen widgets. It keeps itself correct at midnight and on Monday without opening the app.',
      alt: 'Two home screen widgets: a small one with the weekly goal ring and streak, and a medium one that adds the book you’re reading.',
    },
  },

  data: {
    label: 'Your data stays yours',
    heading: 'Backed up as plain text, in your iCloud.',
    paragraphs: [
      'Every book becomes a readable text file in iCloud Drive under Typesetter, sorted into Bookshelf, Currently Reading and Finished Books, with a reading log alongside. Automatic after each change, and restorable on a new phone.',
      'Your notes are never locked in a format only the app can read. Open them in any text editor, on any device, in ten years.',
    ],
    facts: [
      { title: 'No account', body: 'Nothing to sign up for. The app never asks who you are.' },
      { title: 'No tracking', body: 'No analytics, no ads, nothing sent anywhere.' },
      { title: 'Works fully offline', body: 'iCloud is the backup, not a requirement.' },
    ],
    link: 'Read the privacy policy',
    filesAlt:
      'The Files app showing iCloud Drive, Typesetter, with folders for Bookshelf, Currently Reading and Finished Books and a Reading Log text file.',
    fileAlt:
      'A plain text file for Emma listing author, year, pages, status, current page, and the notes beneath.',
  },

  also: {
    label: 'Also in the app',
    heading: 'Small things done properly.',
    cards: [
      {
        label: 'Recommendations',
        heading: 'From what you’ve finished.',
        body: 'More by the authors you’ve read, popular titles in your favourite genres, and a few curated lists.',
      },
      {
        label: 'Undo, not nagging',
        heading: 'Delete a book, then change your mind.',
        body: 'An undo after deleting instead of a confirmation dialog. Haptics you can switch off.',
      },
      {
        label: 'Native throughout',
        heading: 'Feels like it came with the phone.',
        body: 'A native iOS tab bar with Liquid Glass on iOS 26. Text scales with your accessibility settings.',
      },
    ],
  },

  finalCta: {
    label: 'Get the app',
    supporting: 'On the App Store for iPhone and iPad. iOS 18 or later.',
  },

  footer: {
    privacy: 'Privacy policy',
    support: 'Support',
    coffee: 'Buy me a coffee',
    facts: [
      'Typesetter: Book Notes',
      'iOS 18 or later',
      'iPhone and iPad',
      'Built by an independent developer',
    ],
  },

  privacyPolicy: {
    updated: '6 October 2026',
    intro:
      'Typesetter is built so that your reading stays yours. This page explains what the app stores, where it goes, and what leaves your phone. The short version: everything lives on your device and in your own iCloud Drive, there is no account and no analytics, and the only thing the app sends anywhere is a book lookup to Open Library when you ask for one.',
    summary: [
      { title: 'No account', body: 'Nothing to sign up for. The app never asks for your name, email address or Apple ID.' },
      { title: 'No analytics or tracking', body: 'No usage analytics, no crash reporting service, no advertising identifiers, no third-party SDKs that phone home.' },
      { title: 'Your files, in your iCloud', body: 'Backups are plain text files in your own iCloud Drive, under Apple’s terms, readable without the app.' },
    ],
    sections: [
      {
        heading: 'What the app stores',
        paragraphs: [
          'Your library: the books you add, with title, author, year, page count, cover image and which shelf they are on. Your notes for each book. Your reading sessions, with start time, duration and the page you logged. Your weekly goal, streak history and settings such as reminder times and haptics.',
          'All of this is kept on your iPhone or iPad in the app’s own storage. Deleting the app deletes it from the device. If you use iCloud device backup, it is included in that backup like any other app data.',
        ],
      },
      {
        heading: 'iCloud Drive backup',
        paragraphs: [
          'When iCloud Drive is available, the app writes a readable text file for every book into a Typesetter folder in your iCloud Drive, sorted into Bookshelf, Currently Reading and Finished Books, with a reading log alongside. It updates these files after each change and reads them back to restore your library on a new device.',
          'These files are in your own iCloud account, protected and governed by Apple’s iCloud terms and privacy policy. The developer has no access to them and no server of their own. You can open, copy, move or delete them in the Files app at any time. If iCloud Drive is off or you are offline, the app works exactly the same and simply keeps everything on the device.',
        ],
      },
      {
        heading: 'Book lookups and recommendations',
        paragraphs: [
          'When you scan a barcode or search for a title, the app sends the ISBN or the words you typed to Open Library (openlibrary.org), a non-profit project of the Internet Archive, to fetch the title, author, year, page count and cover. Recommendations work the same way: the app asks Open Library for more books by an author you have read or popular titles in a genre. These requests contain no personal identifier, no account, and nothing about your notes or reading time. Open Library sees them the way it sees any website visit, including your IP address, under its own privacy policy.',
          'You can add every book by hand without any lookup, and the app never contacts Open Library unless you use one of these features.',
        ],
      },
      {
        heading: 'Camera and passage capture',
        paragraphs: [
          'Point the camera at a page and the app recognises the text on your device using Apple’s built-in text recognition. Only the recognised text is added to your notes. No photo is saved and nothing is uploaded. Camera access is optional and can be withdrawn in Settings.',
        ],
      },
      {
        heading: 'Notifications, Live Activities and widgets',
        paragraphs: [
          'Reminders, the streak saver and the Sunday check-in are optional. They are scheduled locally on your device and involve no server. The reading timer’s Live Activity and the home screen and lock screen widgets read the same local data as the app and never send it anywhere.',
        ],
      },
      {
        heading: 'Sharing and export',
        paragraphs: [
          'When you share a book’s notes or export your library, the app hands the text to the iOS share sheet and you choose where it goes. Nothing is shared unless you start it.',
        ],
      },
      {
        heading: 'Purchases',
        paragraphs: [
          'Any purchase is handled by the App Store. The app never sees your payment details, and the developer receives only the anonymous transaction record Apple provides.',
        ],
      },
      {
        heading: 'Children',
        paragraphs: [
          'The app is not directed at children under 13 and collects no personal information from anyone.',
        ],
      },
      {
        heading: 'Your choices',
        paragraphs: [
          'Delete a book to remove it and its notes from the device and, on the next sync, from your iCloud Drive folder. Delete the app to remove everything it stores on the device; the text files in iCloud Drive stay yours until you delete them in Files. Turn off camera access, notifications or iCloud Drive for the app in Settings at any time.',
        ],
      },
      {
        heading: 'This website and links to other sites',
        paragraphs: [
          'taci.uk uses Google Analytics to count visits and see which pages are read. It sets cookies in your browser and records the page you view, the site that linked you here, your browser, device type and approximate location (worked out from your IP address, which Google does not store). It is used only on this website, never inside the app, and is not linked to anything you do in the app. You can block it with a content blocker or Google’s opt-out add-on at tools.google.com/dlpage/gaoptout. The Buy me a coffee link opens buymeacoffee.com, a third party with its own privacy policy; nothing is sent there unless you visit it.',
        ],
      },
      {
        heading: 'Changes to this policy',
        paragraphs: [
          'If the app’s handling of data changes, this page will change with it and the date above will be updated.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [
          'Typesetter is made by Michael Taciuk. Questions about privacy or the app can be sent to the support address below.',
        ],
        contact: true,
      },
    ],
  },
};
